import { Instance, SnapshotIn, SnapshotOut, types, IAnyType } from "mobx-state-tree"
import { RoomGuestModel } from "./guest"
import {
  ITripRoom,
  TripRoomPaymentRequest,
  TripRoomPaymentRequestLineItem,
} from "@cruisen/contract"

const PointPlaneCoordinates = types.model("PointPlaneCoordinates", {
  x: types.number,
  y: types.number,
})

const SquareBoundingBox = types.model("SquareBoundingBox", {
  lowerLeft: PointPlaneCoordinates,
  upperRight: PointPlaneCoordinates,
  center: PointPlaneCoordinates,
})

const ConfirmationFileModel = types.model("ConfirmationFile", {
  url: types.string,
  type: types.string,
})

const RoomBookingDetailsModel = types.model("RoomBookingDetailsModel").props({
  status: types.optional(types.string, "booked"),
  confirmationFile: types.maybeNull(ConfirmationFileModel),
  cabinName: types.maybeNull(types.string),
  cabinNo: types.optional(types.string, ""),
  cabinGradeCode: types.maybeNull(types.string),
  cabinFareCode: types.maybeNull(types.string),
  deckId: types.optional(types.number, 0),
  cabinPosition: types.maybeNull(SquareBoundingBox),
  deckName: types.optional(types.string, ""),
  cabinGrade: types.optional(types.string, ""),
})

const RoomPaymentRequestLineItemModel = types
  .model("RoomPaymentRequestLineItemModel")
  .props<{ [key in keyof TripRoomPaymentRequestLineItem]: IAnyType }>({
    id: types.string,
    type: types.string,
    total: types.maybeNull(types.string),
    paymentPlan: types.maybeNull(types.string),
    label: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
    created: types.optional(types.integer, () => Date.now()),
    updated: types.optional(types.integer, () => Date.now()),
    meta: types.map(types.union(types.string, types.number, types.null)),
  })

const RoomPaymentRequestModel = types
  .model("RoomPaymentRequestModel")
  .props<{ [key in keyof TripRoomPaymentRequest]: IAnyType }>({
    id: types.string,
    status: types.string,
    senderId: types.string,
    assignedTo: types.array(types.string),
    isDemo: types.optional(types.boolean, false),
    lineItems: types.maybeNull(types.array(RoomPaymentRequestLineItemModel)),
    created: types.optional(types.integer, () => Date.now()),
    updated: types.optional(types.integer, () => Date.now()),
  })

export const RoomModel = types.model("Room").props<{ [key in keyof ITripRoom]: IAnyType }>({
  id: types.identifier,
  tripId: types.string,
  name: types.string,
  guests: types.array(RoomGuestModel),
  cabinTypePreferences: types.array(types.string),
  bookingDetails: types.maybeNull(RoomBookingDetailsModel),
  paymentRequests: types.maybeNull(types.map(RoomPaymentRequestModel)),
  created: types.optional(types.integer, () => Date.now()),
})

export type RoomPaymentRequest = Instance<typeof RoomPaymentRequestModel>
export type RoomBookingDetails = Instance<typeof RoomBookingDetailsModel>
export type Room = Instance<typeof RoomModel>
export type RoomSnapshotOut = SnapshotOut<typeof RoomModel>
export type RoomSnapshotIn = SnapshotIn<typeof RoomModel>
